import { ArrowClockwiseIcon } from '@phosphor-icons/react'
import { Button } from '@curri/components'
import { Fragment, useState, type ReactNode } from 'react'

import type { Resource } from '../../api/client.ts'
import {
  DELIVERY_EXTERNAL_ID_PATTERN,
  resendWebhookAttempt,
  WEBHOOK_WRITE_SCOPE,
  type AttemptStatusFilter,
  type WebhookAttempt,
} from '../../api/webhooks.ts'
import { useAuth } from '../../auth/AuthProvider.tsx'
import { useApiFetch, useGhost } from '../../ghost/GhostProvider.tsx'

import {
  AttemptsFrame,
  MetaRow,
  ResponseCell,
  WINDOW_OPTIONS,
  type AttemptColumn,
} from './AttemptsFrame.tsx'
import { describeFailure, formatAbsolute, formatRelative } from './format.ts'
import { PayloadPreview } from './PayloadPreview.tsx'
import { Pill } from './Pill.tsx'

// What was sent to a delivery endpoint, and what came back.
//
// `success` is the source of truth for the outcome, not `httpStatus`: a timeout
// or a connection reset is a failure with no status at all, so inferring failure
// from a 4xx/5xx would silently mark those as successes.

const COLUMNS: AttemptColumn[] = [
  { header: 'Outcome', width: 'minmax(84px, 110px)' },
  { header: 'Sent', width: 'minmax(96px, 120px)' },
  { header: 'Delivery', width: 'minmax(0, 190px)' },
  { header: 'Delivery status', width: 'minmax(120px, 1fr)' },
  { header: 'Response', width: 'minmax(84px, 140px)' },
]

const MONO = {
  fontFamily: 'var(--curri-font-mono)',
  fontSize: 12.5,
} as const

const deliveryReference = (attempt: WebhookAttempt): string | null =>
  attempt.deliveryExternalId ??
  (attempt.deliveryId === null ? null : String(attempt.deliveryId))

const DeliveryCell = ({ attempt }: { attempt: WebhookAttempt }) => {
  const reference = deliveryReference(attempt)

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <code
        style={{
          fontFamily: 'var(--curri-font-mono)',
          fontSize: 12.5,
          color: reference
            ? 'var(--curri-text-secondary)'
            : 'var(--curri-text-tertiary)',
        }}
      >
        {reference ?? 'Not available'}
      </code>
      {attempt.isSandbox && <Pill tone="neutral">Sandbox</Pill>}
    </span>
  )
}

// A status code with a break opportunity after each underscore, so a long one
// like `en_route_to_destination` wraps at a word in a narrow column rather than
// mid-word or not at all.
const breakAtUnderscores = (code: string): ReactNode => {
  const parts = code.split('_')
  return parts.map((part, index) => (
    <Fragment key={index}>
      {index < parts.length - 1 ? `${part}_` : part}
      {index < parts.length - 1 && <wbr />}
    </Fragment>
  ))
}

// The status the body carried, as the sender spelled it: partners match on the
// code, and the docs list the codes, so it is not translated into prose.
const StatusCell = ({ deliveryStatus }: { deliveryStatus: string | null }) => (
  <code
    style={{
      ...MONO,
      color: deliveryStatus
        ? 'var(--curri-text-secondary)'
        : 'var(--curri-text-tertiary)',
    }}
  >
    {deliveryStatus === null ? 'Not available' : breakAtUnderscores(deliveryStatus)}
  </code>
)

/**
 * Pins the table to one delivery.
 *
 * The service matches the id exactly, so a partial id is held back rather than
 * sent: every keystroke of a full id would otherwise be a valid-looking query
 * that returns nothing, and the table would flicker empty while typing. The
 * hint names the shape so a pasted id missing its prefix is explained.
 */
const DeliveryIdFilter = ({
  onChange,
  value,
}: {
  onChange: (next: string) => void
  value: string
}) => {
  const trimmed = value.trim()
  const isIncomplete = trimmed !== '' && !DELIVERY_EXTERNAL_ID_PATTERN.test(trimmed)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <input
        aria-label="Filter by delivery id"
        className="webhook-filter"
        onChange={event => onChange(event.target.value)}
        placeholder="Delivery id, e.g. del_1P9xKq2eZvKY"
        spellCheck={false}
        type="text"
        value={value}
      />
      {isIncomplete && (
        <span
          role="status"
          style={{ fontSize: 11.5, color: 'var(--curri-text-tertiary)' }}
        >
          Enter a full id starting with del_
        </span>
      )}
    </div>
  )
}

const SentCell = ({ sentAt }: { sentAt: string | null }) => {
  if (!sentAt) {
    return <span style={{ color: 'var(--curri-text-tertiary)' }}>Unknown</span>
  }

  return (
    <span style={{ whiteSpace: 'nowrap' }} title={formatAbsolute(sentAt)}>
      {formatRelative(sentAt)}
    </span>
  )
}

// The design's response pane lists duration, retry count, and a signature; none
// of those are recorded, so this shows only what the API actually returns.
const ResponseMeta = ({ attempt }: { attempt: WebhookAttempt }) => (
  <div
    style={{
      border: '1px solid var(--curri-border)',
      borderRadius: 12,
      padding: '14px 16px 6px',
    }}
  >
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'var(--curri-text-tertiary)',
      }}
    >
      Response
    </div>
    <MetaRow
      color={
        attempt.success ? 'var(--color-text-success)' : 'var(--color-text-danger)'
      }
      label="Status"
      value={
        attempt.httpStatus === null
          ? `No response${attempt.statusText ? ` · ${attempt.statusText}` : ''}`
          : `${attempt.httpStatus}${attempt.statusText ? ` ${attempt.statusText}` : ''}`
      }
    />
    <MetaRow
      label="Sent"
      value={attempt.sentAt ? formatAbsolute(attempt.sentAt) : 'Unknown'}
    />
    <MetaRow label="Delivery" value={deliveryReference(attempt) ?? 'Not available'} />
    <MetaRow label="Mode" value={attempt.isSandbox ? 'Sandbox' : 'Production'} />
  </div>
)

type ResendState =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'queued' }
  | { status: 'failed'; message: string }

/**
 * Queue a redelivery for this attempt's delivery.
 *
 * Rendered only when the session holds webhook:write — a session without it
 * (an OAuth client not yet re-registered) gets no dead button to click.
 * Disabled while ghosting rather than hidden: the server refuses ghosted
 * writes (`actAsUser` is read-only), and a support engineer should see that
 * the action exists without being handed a 403.
 */
const ResendControl = ({ attempt }: { attempt: WebhookAttempt }) => {
  const apiFetch = useApiFetch()
  const { target: ghostTarget } = useGhost()
  const [state, setState] = useState<ResendState>({ status: 'idle' })

  const resend = async () => {
    setState({ status: 'sending' })
    const result = await resendWebhookAttempt(apiFetch, attempt.id)
    setState(
      result.ok
        ? { status: 'queued' }
        : { status: 'failed', message: describeFailure(result.failure).body }
    )
  }

  const isGhosting = ghostTarget !== null

  return (
    <div
      style={{
        flexBasis: '100%',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 12,
      }}
    >
      <Button
        disabled={isGhosting || state.status === 'queued'}
        loading={state.status === 'sending'}
        onClick={() => void resend()}
        size="small"
        variant="secondary"
      >
        <ArrowClockwiseIcon />
        {state.status === 'queued' ? 'Queued' : 'Resend'}
      </Button>
      <span
        style={{
          fontSize: 12.5,
          color:
            state.status === 'failed'
              ? 'var(--color-text-danger)'
              : 'var(--curri-text-tertiary)',
        }}
      >
        {isGhosting
          ? 'Read-only while viewing as a partner.'
          : state.status === 'queued'
            ? 'Queued — the webhook is regenerated from the delivery’s current state, and the outcome will appear as a new attempt.'
            : state.status === 'failed'
              ? state.message
              : 'Sends a fresh webhook for this delivery, reflecting its current status.'}
      </span>
    </div>
  )
}

const AttemptDetail = ({
  attempt,
  canResend,
}: {
  attempt: WebhookAttempt
  canResend: boolean
}) => (
  <div
    style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 16,
      // The left inset (30px chevron column + 12px gap) tucks the detail under
      // the row's content rather than its affordance.
      padding: '9px 12px 18px 42px',
    }}
  >
    <div style={{ flex: '1.35 1 320px', minWidth: 0 }}>
      <PayloadPreview payload={attempt.payload} />
    </div>
    <div style={{ flex: '1 1 240px', minWidth: 0 }}>
      <ResponseMeta attempt={attempt} />
    </div>
    {/* No delivery id means nothing to re-drive a send for — no control. */}
    {canResend && attempt.deliveryId !== null && (
      <ResendControl attempt={attempt} />
    )}
  </div>
)

const AttemptCells = ({ attempt }: { attempt: WebhookAttempt }) => (
  <>
    <span role="cell" style={{ justifySelf: 'start' }}>
      <Pill tone={attempt.success ? 'success' : 'danger'}>
        {attempt.success ? 'Delivered' : 'Failed'}
      </Pill>
    </span>
    <span role="cell" style={{ fontSize: 14 }}>
      <SentCell sentAt={attempt.sentAt} />
    </span>
    <span role="cell">
      <DeliveryCell attempt={attempt} />
    </span>
    <span role="cell" style={{ minWidth: 0 }}>
      <StatusCell deliveryStatus={attempt.deliveryStatus} />
    </span>
    <span role="cell" style={{ ...MONO, textAlign: 'right' }}>
      <ResponseCell httpStatus={attempt.httpStatus} statusText={attempt.statusText} />
    </span>
  </>
)

// Pinned to a delivery, the service reads its whole retained history rather
// than the window, so the empty copy must not name the window.
const emptyCopyFor = ({
  deliveryExternalId,
  status,
  windowDays,
}: {
  deliveryExternalId: string | undefined
  status: AttemptStatusFilter
  windowDays: number
}): { title: string; body: string } => {
  const outcome = status === 'all' ? '' : `${status} `

  if (deliveryExternalId !== undefined) {
    return {
      title: `No ${outcome}attempts for ${deliveryExternalId}`,
      body: 'The filter matches one delivery id exactly, across its whole retained history. Check the id against your bookings, or clear it to see every attempt.',
    }
  }

  return {
    title:
      status === 'all'
        ? `Nothing sent in the last ${windowDays} days`
        : `No ${outcome}attempts in the last ${windowDays} days`,
    body:
      status === 'all'
        ? 'Curri has not sent a delivery webhook for your account in this window. Note that itinerary webhooks are not recorded, so this is not proof that nothing at all was sent.'
        : 'Try a wider window, or clear the filter to see every attempt.',
  }
}

export const AttemptsCard = ({
  deliveryExternalId,
  deliveryQuery,
  isLoadingMore,
  loadMore,
  loadMoreFailed,
  nextCursor,
  onDeliveryQueryChange,
  onStatusChange,
  onWindowChange,
  resource,
  status,
  windowDays,
}: {
  /** The id the feed is pinned to — `deliveryQuery` once it is a complete id. */
  deliveryExternalId: string | undefined
  /** What is in the filter box, complete or not. */
  deliveryQuery: string
  isLoadingMore: boolean
  loadMore: () => void
  loadMoreFailed: boolean
  nextCursor: number | null
  onDeliveryQueryChange: (next: string) => void
  onStatusChange: (next: AttemptStatusFilter) => void
  onWindowChange: (next: number) => void
  resource: Resource<WebhookAttempt[]>
  status: AttemptStatusFilter
  windowDays: number
}) => {
  const { state: authState } = useAuth()

  // Hidden, not disabled, without the scope: a token issued before the OAuth
  // client learned webhook:write would otherwise render a button that can only
  // ever 403.
  const canResend =
    authState.status === 'authenticated' &&
    authState.caller.grantedScopes.includes(WEBHOOK_WRITE_SCOPE)

  const empty = emptyCopyFor({ deliveryExternalId, status, windowDays })

  return (
    <AttemptsFrame
      columns={COLUMNS}
      description="Every delivery webhook Curri sent, with the delivery status it carried and the status your endpoint returned. Open a row for the exact body that was POSTed."
      emptyBody={empty.body}
      emptyTitle={empty.title}
      filters={
        <DeliveryIdFilter onChange={onDeliveryQueryChange} value={deliveryQuery} />
      }
      isLoadingMore={isLoadingMore}
      keyOf={attempt => attempt.id}
      loadMore={loadMore}
      loadMoreFailed={loadMoreFailed}
      nextCursor={nextCursor}
      onStatusChange={onStatusChange}
      onWindowChange={onWindowChange}
      renderDetail={attempt => (
        <AttemptDetail attempt={attempt} canResend={canResend} />
      )}
      renderRow={attempt => <AttemptCells attempt={attempt} />}
      resource={resource}
      rowName={deliveryReference}
      status={status}
      title="Attempts"
      windowDays={windowDays}
      windowOptions={WINDOW_OPTIONS}
    />
  )
}
